import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService, MenuItem } from 'primeng/api';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { Settlement, SettlementSelloutStatus } from '../settlement.model';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { FieldsetModule } from 'primeng/fieldset';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload'
import { forkJoin } from 'rxjs';
import { SettlementService } from '../settlement.service';
import { TableModule } from "primeng/table";
import { SettlementAttachment } from '../settlement-attachment.model';
import { WorkflowApproval } from '../../chat-widgets/approval-card/workflow-apporval.model';
import { ApprovalCardComponent } from "../../chat-widgets/approval-card/approval-card.component";
import { AppUser } from '../../users/user.model';
import { Subscription } from 'rxjs';
import { ChatSession } from '../../models/chat-session.model';
import { ChatSessionService } from '../../services/chat-session.service';
import { WorkflowApprovalService } from '../../chat-widgets/approval-card/workflow-approval.service';
import { HttpParams } from '@angular/common/http';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DropdownModule } from 'primeng/dropdown';
import { AuthenticationService } from '../../services/authentication.service';
import { TooltipModule } from 'primeng/tooltip';
import { AuthorizationService } from '../../services/authorization.service';
import { Supplier } from '../../master-data/suppliers/suppliers.model';
import { SuppliersService } from '../../master-data/suppliers/suppliers.service';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAX_FILE_SIZE_BYTES } from '../../shared/constants/file-size.constants';
import { GENERAL_UPLOAD_ACCEPT_TYPES } from '../../shared/constants/upload-file-types.constants';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { ProgressBarModule } from 'primeng/progressbar';
import { filter, map, take } from 'rxjs/operators';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { toNormalizedDate } from '../../shared/utils/date-normalization.util';

@Component({
  selector: 'app-view-settlement',
  standalone: true,
  imports: [
    CommonModule,
    ToastModule,
    ProgressSpinnerModule,
    TagModule,
    FieldsetModule,
    BreadcrumbModule,
    ButtonModule,
    FileUploadModule,
    TableModule,
    ApprovalCardComponent,
    ChipModule,
    TooltipModule,
    DropdownModule,
    DialogModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumberModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    CurrencyPipe,
    NgxExtendedPdfViewerModule,
    ProgressBarModule,
    AppDatePipe
  ],
  providers: [MessageService, CurrencyPipe],
  templateUrl: './view-settlement.component.html',
  styleUrls: ['./view-settlement.component.css']
})
export class ViewSettlementComponent implements OnInit, OnDestroy {
  readonly maxFileSize = MAX_FILE_SIZE_BYTES;
  readonly attachmentAcceptTypes = GENERAL_UPLOAD_ACCEPT_TYPES;

  private sessionRouteSub!: Subscription;
  private settlementRouteSub!: Subscription;
  private sessionDataSub!: Subscription;
  private authSubscription!: Subscription;
  currentSessionId: number | null = null;
  currentSession: ChatSession | undefined;

  currentUser: AppUser | null = null;
  settlement: Settlement | null = null;
  selloutStatuses: SettlementSelloutStatus[] = [];
  isLoading = false;
  isLoadingSelloutStatuses = false;
  selloutStatusesError = false;
  isSaving = false;
  settlementId: number | null = null;
  attachmentsChanged = false;
  isUploadingAttachments = false;
  isSavingAttachments = false;
  attachmentUploadProgress = 0;
  breadcrumbItems: MenuItem[] = [];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  canUpdateBudgetaryControl: boolean = false;
  canUpdateFinance: boolean = false;
  displayApDialog = false;
  displayArDialog = false;

  pdfViewerVisible = false;
  pdfSrc: string | null = null;
  imageViewerVisible = false;
  officeViewerVisible = false;
  emlViewerVisible = false;
  imageSrc: string | null = null;
  officeSrc: SafeResourceUrl | null = null;
  emlSrc: SafeResourceUrl | null = null;

  apDetailsForm!: FormGroup;
  arFinanceForm!: FormGroup;

  suppliers: Supplier[] = [];
  filteredSuppliers: Supplier[] = [];

  // Cache for storing fetched pre-signed URLs to avoid redundant API calls.
  private attachmentUrlCache = new Map<number, { url: string; expires: number }>();
  private attachmentProgressByIndex = new Map<number, number>();

  constructor(
    private route: ActivatedRoute, private router: Router,
    private messageService: MessageService,
    private authService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private chatSessionService: ChatSessionService,
    private settlementService: SettlementService,
    private workflowApprovalService: WorkflowApprovalService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private supplierService: SuppliersService,
    private storageService: FirebaseStorageService,
  ) { }

  ngOnInit(): void {
    this.authSubscription = this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.checkPermissions();
      }
    });

    // The traversal logic is confirmed to be working. We can remove the verbose logging.
    let routeWithSessionId: ActivatedRoute | null = this.route;
    while (routeWithSessionId && !routeWithSessionId.snapshot.paramMap.has('sessionId')) {
      routeWithSessionId = routeWithSessionId.parent;
    }

    this.sessionRouteSub = (routeWithSessionId || this.route).paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId');

      if (sessionIdParam) {
        this.currentSessionId = +sessionIdParam;
        this.sessionDataSub?.unsubscribe();
        this.sessionDataSub = this.chatSessionService.sessions$.subscribe(sessions => {
          this.currentSession = sessions.find(s => s.sessionId === this.currentSessionId);
        });
      } else {
        const errorMsg = 'Session ID not found in route parameters.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[ViewSettlementComponent] ERROR: ${errorMsg}`);
      }
    });

    let routeWithPromoPlanId: ActivatedRoute | null = this.route;
    while (routeWithPromoPlanId && !routeWithPromoPlanId.snapshot.paramMap.has('settlementId')) {
      routeWithPromoPlanId = routeWithPromoPlanId.parent;
    }

    this.settlementRouteSub = (routeWithPromoPlanId || this.route).paramMap.subscribe(params => {
      const settlementIdParam = params.get('settlementId');

      if (settlementIdParam) {
        this.settlementId = +settlementIdParam;
        this.loadSettlementData();
      } else {
        const errorMsg = 'Settlement ID not found in route parameters. Cannot load data.';
        this.messageService.add({ severity: 'error', summary: 'Routing Error', detail: errorMsg });
        console.error(`[ViewSettlementComponent] ERROR: ${errorMsg}`);
      }
    });

    this.initForms();
    this.loadSuppliers();

    this.apDetailsForm.get('serviceInvoice')?.valueChanges.subscribe(() => this.updateApFormValues());
  }

  ngOnDestroy(): void {
    this.sessionRouteSub?.unsubscribe();
    this.settlementRouteSub?.unsubscribe();
    this.sessionDataSub?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  /**
   * Checks the current user's permissions and sets boolean flags
   * to be used in the template for showing/hiding/disabling UI elements.
   */
  private checkPermissions(): void {
    // Check for 'update' permission for the edit buttons.
    this.canUpdateBudgetaryControl = this.authorizationService.hasAnyRole(this.currentUser, ['Budgetary Control']);
    this.canUpdateFinance = this.authorizationService.hasAnyRole(this.currentUser, ['Finance AR']);

    // Check for 'update' permission for the edit buttons.
    //this.canUpdateBudgetaryControl = true;
    //this.canUpdateFinance = true;
  }

  private initForms(): void {
    this.apDetailsForm = this.fb.group({
      apVoucher: [''],
      apValue: [0],
      apVat: [0],
      apTotalAmount: [{ value: 0, disabled: true }],
      apArGlCodeCombination: [''],
      apApGlCodeCombination: [''],
      apTextReference: [''],
      serviceInvoice: [''],
      supplier: [''],
      arTrxNo: [''],   
    });
    
    this.arFinanceForm = this.fb.group({
      // arTrxNo: [''],
      arCmHash: [''],
    });

    // Add subscriptions to calculate total for AP form
    this.apDetailsForm.get('apValue')?.valueChanges.subscribe(() => this.calculateApTotal());
    this.apDetailsForm.get('apVat')?.valueChanges.subscribe(() => this.calculateApTotal());
  }

  private updateApFormValues(): void {
    if (!this.settlement) {
      return;
    }

    const isServiceInvoice = this.apDetailsForm.get('serviceInvoice')?.value === 'Yes';
    const settlement = this.settlement;

    // Build AR GL Code
    const arAccount = isServiceInvoice
      ? settlement.activityType?.serviceInvoiceAccount
      : settlement.activityType?.arAccount;

    const arBuilder = [
      arAccount ?? '',
      settlement.business?.arAccount ?? '',
      settlement.businessUnit?.arAccount ?? '',
      '00000',
      settlement.branch?.arAccount ?? '',
      '0000',
      '00',
      '03',
      settlement.activityType?.arFuture1 ?? '',
      settlement.activityType?.arFuture2 ?? ''
    ].join('-');

    // Build AP GL Code
    const apBuilder = [
      settlement.activityType?.apAccount ?? '',
      settlement.business?.apAccount ?? '',
      settlement.businessUnit?.apAccount ?? '',
      '00000',
      settlement.branch?.apAccount ?? '',
      '0000',
      '00',
      '03',
      settlement.activityType?.apFuture1 ?? '',
      settlement.activityType?.apFuture2 ?? ''
    ].join('-');

    // Build Text Reference
    const actualEndDate = toNormalizedDate(settlement.actualEndDate);
    const textReferenceBuilder = `${settlement.business?.businessName ?? ''}-${settlement.id}-${settlement.activityType?.name ?? ''}-${actualEndDate?.getFullYear() ?? '0'}-${actualEndDate ? actualEndDate.getMonth() + 1 : '0'}-${settlement.customerInvoiceNumber ?? '0'}`;

    this.apDetailsForm.patchValue({
      apArGlCodeCombination: arBuilder,
      apApGlCodeCombination: apBuilder,
      apTextReference: textReferenceBuilder,
    });
  }

  private loadSuppliers(): void {
    this.supplierService.getAllSuppliers({ page: 0, size: 1000 }).subscribe(page => {
      this.suppliers = page.content;
      this.filteredSuppliers = page.content;
    });
  }

  private loadSettlementData(): void {
    if (!this.settlementId) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No settlement ID provided.' });
      return;
    }

    this.isLoading = true;
    forkJoin({
      settlement: this.settlementService.getSettlementById(+this.settlementId),
      approvals: this.workflowApprovalService.getAllHistory(new HttpParams({
        fromObject: {
          resourceTypeId: 2, // Assuming 2 is the resource type for Settlement
          businessObjectId: this.settlementId
        }
      }))
    }).subscribe({
      next: ({ settlement, approvals }) => {
        this.settlement = settlement;
        this.settlement!.workflowApprovals = approvals.content;
        this.loadSelloutStatuses();
      },
      complete: () => this.isLoading = false,
      error: () => this.isLoading = false,
    })
  }

  private loadSelloutStatuses(): void {
    if (!this.settlementId) {
      return;
    }

    this.isLoadingSelloutStatuses = true;
    this.selloutStatusesError = false;
    this.settlementService.getSelloutStatuses(this.settlementId).subscribe({
      next: (selloutStatuses) => {
        this.selloutStatuses = selloutStatuses;
      },
      error: (error) => {
        this.selloutStatuses = [];
        this.selloutStatusesError = true;
        console.error('Error loading sellout statuses from BigQuery:', error);
      },
      complete: () => this.isLoadingSelloutStatuses = false
    });
  }

  private calculateApTotal(): void {
    const apValue = this.apDetailsForm.get('apValue')?.value || 0;
    const apVat = this.apDetailsForm.get('apVat')?.value || 0;
    this.apDetailsForm.get('apTotalAmount')?.setValue(apValue + apVat, { emitEvent: false });
  }

  onStatusChange(status: WorkflowApproval) {
    this.settlement!.status = status.workflowStatus;
    this.workflowApprovalService.getAllHistory(new HttpParams({
      fromObject: {
        resourceTypeId: 2, // Assuming 2 is the resource type for Settlement
        businessObjectId: this.settlementId!
      }
    })).subscribe({
      next: (approvals) => {
        this.settlement!.workflowApprovals = approvals.content;
      },
      error: (err) => {
        console.error('Error fetching approvals:', err);
      }
    });
  }

  getStatusSeverity(): string {
    switch (this.settlement?.status?.statusName) {
      case 'Completed':
      case 'End':
        return 'success';
      case 'Pending - Sales':
      case 'Pending - Marketing':
        return 'warn';
      case 'Cancelled':
        return 'danger';
      case 'Start':
      case 'Draft':
      case 'Pending - Budgetary Control':
      case 'Pending - A&P Controller':
      case 'Pending - Finance AR':
      default:
        return 'info';
    }
  }

  get validatedSellout(): SettlementSelloutStatus | undefined {
    return this.selloutStatuses.find(s => s.validationStatus === 'MATCHED' || s.validationStatus === 'VALIDATED' || s.validationStatus === 'VARIANCE_OVERRIDE') || this.selloutStatuses[0];
  }

  viewSellout(selloutId: number): void {
    this.router.navigate(['.', { outlets: { canvasOutlet: ['sellout', 'view', selloutId] } }], { relativeTo: this.route.parent?.parent });
  }

  getHubValidationStatusSeverity(status?: string): string {
    switch (status?.toUpperCase()) {
      case 'MATCHED':
      case 'VALIDATED':
        return 'success';
      case 'VARIANCE_OVERRIDE':
        return 'contrast';
      case 'VARIANCE_FOUND':
        return 'warning';
      case 'NOT_MATCHED':
        return 'danger';
      default:
        return 'info';
    }
  }

  getSelloutWorkflowStatusSeverity(status?: string): string {
    switch (status?.toLowerCase()) {
      case 'settled':
      case 'completed':
      case 'approved':
        return 'success';
      case 'pending':
      case 'draft':
        return 'warning';
      case 'cancelled':
      case 'rejected':
        return 'danger';
      default:
        return 'info';
    }
  }

  getSelloutStatusSeverity(status?: string): string {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'not completed':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  /**
   * Navigates to the associated Promo Plan view, respecting the current session context.
   */
  navigateToPromoPlan() {
    if (this.settlement?.promoPlanId && this.currentSessionId !== null) {
      // Use an absolute path within the named outlet, preserving the current session
      this.router.navigate(
        [
          '/ai-studio/session',
          this.currentSessionId,
          { outlets: { canvasOutlet: ['promo-plan', 'view'] } },
        ],
        {
          queryParams: { promoPlanId: this.settlement?.promoPlanId },
        }
      );
    } else {
      this.messageService.add({ severity: 'warn', summary: 'Navigation Error', detail: 'Cannot navigate to Promo Plan: Missing Promo Plan ID or Session ID.' });
    }
  }

  createSellout() {
    if (!this.settlement?.id || this.currentSessionId === null) {
      this.messageService.add({ severity: 'warn', summary: 'Navigation Error', detail: 'Cannot create sellout without a settlement ID and session ID.' });
      return;
    }

    this.router.navigate(
      [
        '/ai-studio/session',
        this.currentSessionId,
        { outlets: { canvasOutlet: ['sellout', 'upload', this.settlement.id] } },
      ]
    );
  }

  onFileSelect(event: any): void {
    if (!this.settlement) return;
    const files: File[] = Array.isArray(event.files)
      ? event.files
      : Array.from(event.files ?? []);
    if (files.length === 0) {
      return;
    }

    if (!this.settlement.attachments) {
      this.settlement.attachments = [];
    }

    const settlementFolder = `settlements/${this.settlement.id ?? this.settlementId ?? 'draft'}`;
    this.isUploadingAttachments = true;
    this.attachmentUploadProgress = 0;
    this.attachmentProgressByIndex.clear();

    forkJoin(
      files.map((file, index) => {
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
        const storagePath = `${settlementFolder}/${Date.now()}-${safeFileName}`;
        return this.storageService.uploadFileWithProgress(file, storagePath).pipe(
          filter(event => {
            this.updateAttachmentProgress(index, event.progress, files.length);
            return !!event.downloadURL;
          }),
          take(1),
          map(event => ({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileUrl: event.downloadURL!,
          } as SettlementAttachment))
        );
      })
    ).subscribe({
      next: (uploadedAttachments) => {
        this.settlement?.attachments?.push(...uploadedAttachments);
        this.attachmentsChanged = true;
        this.isUploadingAttachments = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Files uploaded. Click save to attach them to the settlement.' });
      },
      error: (err) => {
        this.isUploadingAttachments = false;
        this.attachmentUploadProgress = 0;
        console.error('Error uploading attachment to Firebase Storage:', err);
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: 'Could not upload the selected file(s).' });
      }
    });
  }

  private updateAttachmentProgress(index: number, progress: number, totalFiles: number): void {
    this.attachmentProgressByIndex.set(index, progress);
    const totalProgress = Array.from(this.attachmentProgressByIndex.values()).reduce((sum, value) => sum + value, 0);
    this.attachmentUploadProgress = Math.round(totalProgress / Math.max(totalFiles, 1));
  }

  removeAttachment(index: number): void {
    if (this.settlement) {
      this.settlement.attachments?.splice(index, 1);
      this.attachmentsChanged = true;
    }
  }

  saveAttachments(): void {
    if (!this.settlement || !this.settlement.id || !this.settlement.attachments) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'No settlement data available to save attachments.' });
      return;
    }

    const newAttachments = this.settlement.attachments.filter(att => !att.attachmentId && !!att.fileUrl);

    if (newAttachments.length === 0) {
      this.attachmentsChanged = false;
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No new attachments to save.' });
      return;
    }

    this.isSavingAttachments = true;
    this.settlementService.uploadAttachments(this.settlement.id, newAttachments).subscribe({
      next: (savedAttachments) => {
        this.isSavingAttachments = false;
        this.attachmentsChanged = false;
        const existingAttachments = this.settlement!.attachments?.filter(att => att.attachmentId) ?? [];
        this.settlement!.attachments = [...existingAttachments, ...savedAttachments];
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Attachments saved successfully!' });
      },
      error: (err) => {
        this.isSavingAttachments = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save attachments.' });
        console.error('Error saving attachments:', err);
      }
    });
  }

  formatFileSize(bytes: number | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * A wrapper method that efficiently gets a secure URL (from cache or server)
   * and then performs the desired action (view, download, or email).
   */
  private _executeAttachmentAction(attachment: SettlementAttachment, actionType: 'view' | 'download' | 'email', targetWindow?: Window | null): void {
    // Action: DOWNLOAD on a LOCAL file (not yet saved).
    // This can be handled immediately without a server call.
    if (actionType === 'download' && attachment.fileContent) {
      this.downloadLocalAttachment(attachment, targetWindow);
      return;
    }

    if (attachment.fileUrl && !attachment.attachmentId) {
      this._performActionWithUrl(actionType, attachment, attachment.fileUrl, targetWindow);
      return;
    }

    // For all other actions on saved files, we need a secure URL.
    if (!this.settlementId || !attachment.attachmentId) {
      const message = actionType === 'email'
        ? 'Please save new attachments before sharing them via email.'
        : 'This action is not available for unsaved attachments.';
      targetWindow?.close();
      this.messageService.add({ severity: 'warn', summary: 'Action Unavailable', detail: message });
      return;
    }

    // Check cache for a valid, non-expired URL.
    const cachedUrl = this.attachmentUrlCache.get(attachment.attachmentId);
    if (cachedUrl && cachedUrl.expires > Date.now()) {
      this._performActionWithUrl(actionType, attachment, cachedUrl.url, targetWindow);
      return;
    }

    // If not in cache or expired, fetch a new URL from the server.
    this.messageService.add({ severity: 'info', summary: 'Loading', detail: 'Generating secure link...', life: 2000 });
    this.settlementService.getAttachmentViewUrl(this.settlementId, attachment.attachmentId).subscribe({
      next: (response) => {
        if (response && response.url) {
          // Cache the new URL for 5 minutes.
          const expirationTime = Date.now() + 5 * 60 * 1000;
          this.attachmentUrlCache.set(attachment.attachmentId!, { url: response.url, expires: expirationTime });
          this._performActionWithUrl(actionType, attachment, response.url, targetWindow);
        } else {
          targetWindow?.close();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to retrieve a valid URL.' });
        }
      },
      error: (err) => {
        console.error(`Error getting attachment URL for ${actionType}:`, err);
        targetWindow?.close();
        this.messageService.add({ severity: 'error', summary: 'Access Denied', detail: 'Could not get permission for the file.' });
      }
    });
  }

  /** Helper to execute the final action once a secure URL is obtained. */
  private _performActionWithUrl(actionType: 'view' | 'download' | 'email', attachment: SettlementAttachment, url: string, targetWindow?: Window | null): void {
    switch (actionType) {
      case 'view':
        const fileName = (attachment.fileName || '').toLowerCase();
        if (fileName.endsWith('.pdf')) {
          this.pdfSrc = url;
          this.pdfViewerVisible = true;
        } else if (fileName.match(/\.(jpeg|jpg|gif|png|svg|webp)$/)) {
          this.imageSrc = url;
          this.imageViewerVisible = true;
        } else if (fileName.match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/)) {
          const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
          this.officeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(officeViewerUrl);
          this.officeViewerVisible = true;
        } else if (fileName.endsWith('.eml')) {
          this.emlSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          this.emlViewerVisible = true;
        } else {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'download':
        this.openUrlInAttachmentTab(url, targetWindow);
        break;
      case 'email':
        const subject = `Attachment: ${attachment.fileName} from Promo Plan #${this.settlement?.promoPlanId}`;
        let body = `Please find the attached file: ${attachment.fileName}.`;
        body += `\n\nYou can view or download it from this secure link: ${url}`;
        const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoLink;
        break;
    }
  }

  viewAttachment(attachment: SettlementAttachment): void {
    this._executeAttachmentAction(attachment, 'view');
  }

  private openBlankAttachmentTab(): Window | null {
    const targetWindow = window.open('about:blank', '_blank');
    if (!targetWindow) {
      this.messageService.add({ severity: 'warn', summary: 'Popup Blocked', detail: 'Please allow popups to download attachments in a new tab.' });
    }
    return targetWindow;
  }

  private openUrlInAttachmentTab(url: string, targetWindow?: Window | null): void {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.opener = null;
      targetWindow.location.href = url;
      return;
    }

    const openedWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      this.messageService.add({ severity: 'warn', summary: 'Popup Blocked', detail: 'Please allow popups to open attachments in a new tab.' });
    }
  }

  downloadAttachment(attachment: SettlementAttachment): void {
    const targetWindow = this.openBlankAttachmentTab();
    if (!targetWindow) {
      return;
    }
    this._executeAttachmentAction(attachment, 'download', targetWindow);
  }

  emailAttachment(attachment: SettlementAttachment): void {
    this._executeAttachmentAction(attachment, 'email');
  }

  /** Helper to download a new, unsaved file directly from its base64 content. */
  private downloadLocalAttachment(attachment: SettlementAttachment, targetWindow?: Window | null): void {
    if (!attachment.fileContent) return;
    try {
      const base64Response = attachment.fileContent.split(',')[1];
      const byteCharacters = atob(base64Response);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.fileType });
      const objectUrl = window.URL.createObjectURL(blob);

      if (targetWindow && !targetWindow.closed) {
        targetWindow.opener = null;
        targetWindow.location.href = objectUrl;
        return;
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.fileName;
      link.click();
    } catch (e) {
      console.error('Error decoding or downloading base64 file:', e);
      this.messageService.add({ severity: 'error', summary: 'Download Failed', detail: 'Could not process the file for download.' });
    }
  }

  openEditApDialog(): void {
    if (this.settlement) {
      const supplierToSet = this.suppliers.find(s => s.id === this.settlement?.supplier?.id);

      this.apDetailsForm.patchValue({
        apVoucher: this.settlement.apVoucher,
        apValue: this.settlement.value,
        apVat: this.settlement.vat,
        apTotalAmount: this.settlement.totalAmount,
        apArGlCodeCombination: this.settlement.proposedArGlCodeCombination,
        apApGlCodeCombination: this.settlement.proposedApGlCodeCombination,
        apTextReference: this.settlement.proposedTextReference,
        serviceInvoice: this.settlement.serviceInvoice ? 'Yes' : 'No',
        supplier: this.settlement.supplierName,
        arTrxNo: this.settlement.arTrxNo,
      });
      this.displayApDialog = true;
    }
  }

  openEditArDialog(): void {
    if (this.settlement) {
      this.arFinanceForm.patchValue(this.settlement);
      this.displayArDialog = true;
    }
  }

  saveApDetails(): void {
    if (!this.settlement || !this.settlement.id) return;
    if (this.apDetailsForm.invalid) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Form', detail: 'Please check the form for errors.' });
      return;
    }
console.log(this.apDetailsForm);

    this.isSaving = true;
    this.settlement.apVoucher = this.apDetailsForm.get('apVoucher')?.value;
    this.settlement.apValue = this.apDetailsForm.get('apValue')?.value;
    this.settlement.apVat = this.apDetailsForm.get('apVat')?.value;
    this.settlement.apTotalAmount = this.apDetailsForm.get('apTotalAmount')?.getRawValue();
    this.settlement.apArGlCodeCombination = this.apDetailsForm.get('apArGlCodeCombination')?.value;
    this.settlement.apApGlCodeCombination = this.apDetailsForm.get('apApGlCodeCombination')?.value;
    this.settlement.apTextReference = this.apDetailsForm.get('apTextReference')?.value;
    this.settlement.serviceInvoice = this.apDetailsForm.get('serviceInvoice')?.value === 'Yes' ? true : false;
    this.settlement.supplierName = this.apDetailsForm.get('supplier')?.value;
    this.settlement.arTrxNo = this.apDetailsForm.get('arTrxNo')?.value;
    this.settlement.transactionType = this.settlement.serviceInvoice ? 'Financial Settlement' : 'Sales Discount Credit Note'
    console.log('Saving A&P details for settlement:', this.settlement);
    this.settlementService.updateApValues(this.settlement.id, this.settlement).subscribe({
      next: () => {
        // Merge the updated data into the local settlement object
        this.displayApDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'A&P details updated successfully.' });
        this.isSaving = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update A&P details.' });
        console.error('Error updating A&P details:', err);
        this.isSaving = false;
      }
    });
  }

  saveArFinanceDetails(): void {
    if (!this.settlement || !this.settlement.id) return;
    if (this.arFinanceForm.invalid) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid Form', detail: 'Please check the form for errors.' });
      return;
    }

    this.isSaving = true;
    this.settlement.arCmHash = this.arFinanceForm.get('arCmHash')?.value; 
    // this.settlement.arTrxNo = this.arFinanceForm.get('arTrxNo')?.value;
   

    this.settlementService.updateArValues(this.settlement.id, this.settlement).subscribe({
      next: () => {
        // Merge the updated data into the local settlement object
        this.displayArDialog = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'AR Finance details updated successfully.' });
        this.isSaving = false;
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update AR Finance details.' });
        console.error('Error updating AR Finance details:', err);
        this.isSaving = false;
      }
    });
  }

  openSession() {
    if (this.settlement?.linkedSessionId) {
      this.router.navigate([
        '/ai-studio/session',
        this.settlement.linkedSessionId,
        {
          outlets: {
            canvasOutlet: ['settlement', 'view', this.settlement.id],
          },
        },
      ]);
    } else {
      this.messageService.add({ severity: 'warn', summary: 'No Session', detail: 'No active session to open.' });
    }
  }

  editSettlement() {
    this.router.navigate(
      ['/ai-studio/session', this.currentSessionId, { outlets: { canvasOutlet: ['settlement', 'edit', this.settlementId] } }]
    );
  }

  filterSuppliers(event: any) {
    if (!event.target.value || event.target.value.length === 0) {
      this.filteredSuppliers = this.suppliers;
    } else {
      this.filteredSuppliers = this.suppliers.filter(supplier =>
        supplier.supplierName.toLowerCase().includes(event.target.value.toLowerCase())
        || supplier.supplierNumber?.toLowerCase().includes(event.target.value.toLowerCase())
      );
    }
  }

}
